/**
 * Frontend Validation Utilities
 * Matches backend validation requirements to reduce API calls
 */

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validate email format
 */
export function validateEmail(email: string): ValidationResult {
  const errors: string[] = [];
  
  if (!email) {
    errors.push('Email is required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.push('Invalid email format');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate password format (matches backend requirements)
 */
export function validatePassword(password: string): ValidationResult {
  const errors: string[] = [];
  
  if (!password) {
    errors.push('Password is required');
  } else {
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate full name format (matches backend requirements)
 */
export function validateFullName(fullName: string): ValidationResult {
  const errors: string[] = [];
  
  if (!fullName) {
    errors.push('Full name is required');
  } else {
    if (fullName.length < 2) {
      errors.push('Full name must be at least 2 characters long');
    }
    
    if (fullName.length > 100) {
      errors.push('Full name must be less than 100 characters');
    }
    
    if (!/^[a-zA-Z\s]+$/.test(fullName)) {
      errors.push('Full name can only contain letters and spaces');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate signup form data
 */
export function validateSignupForm(email: string, password: string, fullName: string): ValidationResult {
  const emailValidation = validateEmail(email);
  const passwordValidation = validatePassword(password);
  const fullNameValidation = validateFullName(fullName);
  
  const allErrors = [
    ...emailValidation.errors,
    ...passwordValidation.errors,
    ...fullNameValidation.errors
  ];
  
  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  };
}

/**
 * Validate login form data
 */
export function validateLoginForm(email: string, password: string): ValidationResult {
  const errors: string[] = [];
  
  if (!email) {
    errors.push('Email is required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.push('Invalid email format');
  }
  
  if (!password) {
    errors.push('Password is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Get password strength indicator
 */
export function getPasswordStrength(password: string): {
  strength: 'weak' | 'medium' | 'strong';
  score: number;
  suggestions: string[];
} {
  let score = 0;
  const suggestions: string[] = [];
  
  // Length check
  if (password.length >= 8) score += 1;
  else suggestions.push('Use at least 8 characters');
  
  // Lowercase check
  if (/[a-z]/.test(password)) score += 1;
  else suggestions.push('Add lowercase letters');
  
  // Uppercase check
  if (/[A-Z]/.test(password)) score += 1;
  else suggestions.push('Add uppercase letters');
  
  // Number check
  if (/\d/.test(password)) score += 1;
  else suggestions.push('Add numbers');
  
  // Special character bonus
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score += 1;
  
  let strength: 'weak' | 'medium' | 'strong' = 'weak';
  if (score >= 4) strength = 'strong';
  else if (score >= 2) strength = 'medium';
  
  return { strength, score, suggestions };
}